import { DragEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUserParam, setError } from 'actions';
import { PANELS } from 'reducers/user';
import {
  getLastValidIteration,
  getTotalJobsInTour,
  getTotalJobsInTours,
  getToursDataFromIteration,
} from 'utils/SolutionHelpers';
import { formatPriceWithCurrency } from 'utils/formatter';
import { sprintf } from 'utils/helpers';
import { uploadCsvToAiWorkspace } from 'utils/aiChatUploadHelpers';
import { streamChatMessage, SSE_EVENT_TYPES } from 'utils/aiChatStreamHelpers';
import { createAiWorkspace } from 'utils/aiChatWorkspaceHelpers';
import {
  isAcceptedFileType,
  getUploadErrorBody,
  formatDurationLabel,
  getTourLabel,
  formatMinutesLabel,
  findTightestStop,
  getRandomResponseVariant,
  getVariantLabel,
  getPromptTitle,
  getResponseCard,
} from 'utils/routingAiChatHelpers';
import {
  THREAD_ITEM_TYPES,
  RESPONSE_VARIANTS,
  TIGHT_BUFFER_THRESHOLD_MINUTES,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  type RoutingAiChatTranslation,
  type UnitsTranslation,
  type PromptKey,
  type CardAction,
  type ResponseCardData,
  type ThreadItem,
  type ResponseThreadItem,
  type TraceStep,
  type Summary,
  type SolutionState,
  type UserState,
  type RootState,
  type TourData,
  type ArtifactInfo,
  type ArtifactInfoDataResponse,
} from 'components/Chat/RoutingAIChatTypes';
import axios from 'axios';

const useRoutingAIChat = ({
  routingAiChat,
  units,
}: {
  routingAiChat: RoutingAiChatTranslation;
  units: UnitsTranslation;
}) => {
  const dispatch = useDispatch();
  const solution = useSelector<RootState, SolutionState>(
    ({ solution: stateSolution }) => stateSolution,
  );
  const user = useSelector<RootState, UserState>(({ user: stateUser }) => stateUser);

  const [showPanel, setShowPanel] = useState(false);
  const [draft, setDraft] = useState('');
  const [threadItems, setThreadItems] = useState<ThreadItem[]>([]);
  const [selectedActionsByMessage, setSelectedActionsByMessage] = useState<
    Record<string, PromptKey>
  >({});
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isWorkspaceConnecting, setIsWorkspaceConnecting] = useState(false);

  const nextMessageId = useRef<number>(0);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingCardIdRef = useRef<number | null>(null);
  const streamingPendingReset = useRef(false);
  const nextTraceIdRef = useRef<number>(0);

  const formatFileSize = useCallback((bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

    const sizeUnits = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      sizeUnits.length - 1,
    );
    const value = bytes / 1024 ** exponent;
    const rounded = exponent === 0 ? value.toFixed(0) : value.toFixed(1);

    return `${rounded} ${sizeUnits[exponent]}`;
  }, []);

  const getUploadPromptText = useCallback(
    (file: File) =>
      sprintf(routingAiChat.uploadCsv.uploadedFileTemplate, {
        fileName: file.name,
        fileSize: formatFileSize(file.size),
      }),
    [formatFileSize, routingAiChat.uploadCsv.uploadedFileTemplate],
  );

  const summary = useMemo<Summary>(() => {
    const request = solution.requests?.[solution.show];
    const iteration = getLastValidIteration(request);
    const tourData = getToursDataFromIteration(iteration) as TourData | undefined;

    if (!tourData?.tours?.length) {
      return {
        hasPlan: false,
        key: `no-plan-${solution.show}`,
      };
    }

    const tours = tourData.tours || [];
    const assignedOrders = getTotalJobsInTours(tours) || 0;
    const unassignedOrders = tourData.unassigned?.length || 0;
    const totalOrders = assignedOrders + unassignedOrders;
    const totalCost = formatPriceWithCurrency(
      tourData.statistic?.cost || 0,
      user.currency || 'EUR',
    );
    const totalDuration = formatDurationLabel(tourData.statistic?.duration, units);
    const tightestStop = findTightestStop(tours, tourData.plan?.jobs);
    const maxOrders = Math.max(...tours.map((tour) => getTotalJobsInTour(tour.stops) || 0));
    const avgOrders = tours.length > 0 ? (assignedOrders / tours.length).toFixed(1) : '0.0';
    const rebalancedOrders =
      tours.length > 0 ? (assignedOrders / (tours.length + 1)).toFixed(1) : '0.0';

    return {
      hasPlan: true,
      key: `${solution.show}-${assignedOrders}-${totalOrders}-${tourData.statistic?.cost || 0}`,
      assignedOrders,
      totalOrders,
      totalCost,
      totalDuration,
      tightestStop,
      tourCount: tours.length,
      tourLabel: getTourLabel(tours.length, routingAiChat),
      avgOrders,
      maxOrders,
      rebalancedOrders,
    };
  }, [routingAiChat, solution.requests, solution.show, units, user.currency]);

  const summaryText = useMemo(() => {
    if (!summary.hasPlan) return '';

    return sprintf(routingAiChat.summaryTemplate, {
      tourCount: summary.tourCount,
      tourLabel: summary.tourLabel,
      assignedOrders: summary.assignedOrders,
      totalOrders: summary.totalOrders,
      totalCost: summary.totalCost,
      totalDuration: summary.totalDuration,
    });
  }, [routingAiChat.summaryTemplate, summary]);

  const insightText = useMemo(() => {
    if (!summary.hasPlan) return '';
    if (
      !summary.tightestStop ||
      summary.tightestStop.slackMinutes > TIGHT_BUFFER_THRESHOLD_MINUTES
    ) {
      return routingAiChat.noInsight;
    }

    return sprintf(routingAiChat.insightTemplate, {
      stopNumber: summary.tightestStop.stopNumber,
      tourNumber: summary.tightestStop.tourNumber,
      buffer: formatMinutesLabel(summary.tightestStop.slackMinutes, units),
    });
  }, [routingAiChat, summary, units]);

  const statusMessage = useMemo(() => {
    if (!summary.hasPlan) return routingAiChat.noPlanYet;
    return `${summaryText} ${insightText}`.trim();
  }, [insightText, routingAiChat.noPlanYet, summary.hasPlan, summaryText]);

  const welcomeCard = useMemo<ResponseThreadItem>(
    () => ({
      id: 'welcome-ready-card',
      type: THREAD_ITEM_TYPES.RESPONSE,
      variant: RESPONSE_VARIANTS.READY,
      label: routingAiChat.readyLabel,
      body: statusMessage,
      actions: [],
    }),
    [routingAiChat.readyLabel, statusMessage],
  );

  const syncPanelState = useCallback(
    (nextState: boolean) => {
      dispatch(
        setUserParam({
          openPanel: nextState ? PANELS.ROUTING_AI_CHAT : undefined,
        }),
      );
      setShowPanel(nextState);
    },
    [dispatch],
  );

  const resetConversation = useCallback(() => {
    setThreadItems([]);
    setSelectedActionsByMessage({});
    setDraft('');
    nextMessageId.current = 0;
  }, []);

  const appendWorkspaceError = useCallback(
    (body: string) => {
      setThreadItems((current) => [
        ...current,
        {
          id: nextMessageId.current++,
          type: THREAD_ITEM_TYPES.RESPONSE,
          variant: RESPONSE_VARIANTS.WARNING,
          label: routingAiChat.cardLabels.headsUp,
          body,
          actions: [{ key: 'retryConnection', label: routingAiChat.cardActions.retryConnection }],
        },
      ]);
    },
    [routingAiChat],
  );

  const getWorkspaceErrorBody = useCallback(
    () => `${routingAiChat.workspaceSessionUnavailable} ${routingAiChat.workspaceRetryHint}`,
    [routingAiChat.workspaceRetryHint, routingAiChat.workspaceSessionUnavailable],
  );

  const initWorkspace = useCallback(async () => {
    setIsWorkspaceConnecting(true);
    try {
      const workspaceId = await createAiWorkspace();
      dispatch(setUserParam({ aiChatWorkspaceId: workspaceId }));
      return true;
    } catch {
      const errorBody = getWorkspaceErrorBody();
      appendWorkspaceError(errorBody);
      dispatch(setError(errorBody));
      return false;
    } finally {
      setIsWorkspaceConnecting(false);
    }
  }, [appendWorkspaceError, dispatch, getWorkspaceErrorBody]);

  const handleClose = useCallback(() => syncPanelState(false), [syncPanelState]);

  const handleRestart = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    resetConversation();
    dispatch(setUserParam({ aiChatWorkspaceId: null }));
    await initWorkspace();
  }, [resetConversation, dispatch, initWorkspace]);

  const appendFlow = useCallback(
    (promptKey: PromptKey, promptLabel: string) => {
      const title = getPromptTitle(promptKey, promptLabel, routingAiChat, summary);
      const response = getResponseCard({
        promptKey,
        routingAiChat,
        summary,
        units,
      }) as ResponseCardData;
      const randomVariant = getRandomResponseVariant();
      const randomizedResponse: ResponseCardData = {
        ...response,
        variant: randomVariant,
        label: getVariantLabel(randomVariant, routingAiChat),
      };

      setThreadItems((current) => [
        ...current,
        {
          id: nextMessageId.current++,
          type: THREAD_ITEM_TYPES.PROMPT,
          text: title,
        },
        {
          id: nextMessageId.current++,
          type: THREAD_ITEM_TYPES.RESPONSE,
          ...randomizedResponse,
        },
      ]);
    },
    [routingAiChat, summary, units],
  );

  const handleCardAction = useCallback(
    (messageId: number | string, action: CardAction) => {
      if (action.key === 'retryConnection') {
        initWorkspace().catch(() => {});
        return;
      }

      const promptKey = action.key as PromptKey;
      setSelectedActionsByMessage((current) => ({
        ...current,
        [String(messageId)]: promptKey,
      }));
      appendFlow(action.key, action.label);
    },
    [appendFlow, initWorkspace],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const value = draft.trim();
      if (!value || isStreaming || isWorkspaceConnecting) return;

      if (!user.aiChatWorkspaceId) {
        appendWorkspaceError(getWorkspaceErrorBody());
        return;
      }

      setDraft('');

      const promptId = nextMessageId.current++;
      const streamingCardId = nextMessageId.current++;
      streamingCardIdRef.current = streamingCardId;
      streamingPendingReset.current = false;
      nextTraceIdRef.current = 0;

      setThreadItems((current) => [
        ...current,
        { id: promptId, type: THREAD_ITEM_TYPES.PROMPT, text: value },
        {
          id: streamingCardId,
          type: THREAD_ITEM_TYPES.RESPONSE,
          variant: RESPONSE_VARIANTS.ROUTING,
          label: routingAiChat.cardLabels.routingAi,
          body: '',
          actions: [],
          streaming: true,
          traceSteps: [],
        },
      ]);

      setIsStreaming(true);
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const updateStreamingCard = (updates: Partial<ResponseThreadItem>) => {
        setThreadItems((current) =>
          current.map(
            (item): ThreadItem =>
              item.id === streamingCardId
                ? ({ ...(item as ResponseThreadItem), ...updates } as ResponseThreadItem)
                : item,
          ),
        );
      };

      const pushTraceStep = (step: Omit<TraceStep, 'id'>) => {
        const traceStep: TraceStep = { ...step, id: nextTraceIdRef.current++ };
        setThreadItems((current) =>
          current.map((item): ThreadItem => {
            if (item.id !== streamingCardId) return item;
            const responseItem = item as ResponseThreadItem;
            return {
              ...responseItem,
              traceSteps: [...(responseItem.traceSteps ?? []), traceStep],
            };
          }),
        );
      };

      try {
        await streamChatMessage({
          message: value,
          workspaceId: user.aiChatWorkspaceId,
          signal: controller.signal,
          onEvent: ({ type, payload }: { type: string; payload: any }) => {
            const agentPath: string[] = payload?.data?.agent_path ?? [];
            const isSubAgent = agentPath.length > 1;
            const tier: string = payload?.tier ?? '';

            if (type === SSE_EVENT_TYPES.MESSAGE_CHUNK) {
              if (isSubAgent) return;
              const delta: string = payload?.data?.text_delta ?? '';
              setThreadItems((current) =>
                current.map((item): ThreadItem => {
                  if (item.id !== streamingCardId) return item;
                  const responseItem = item as ResponseThreadItem;
                  const newBody = streamingPendingReset.current ? delta : responseItem.body + delta;
                  streamingPendingReset.current = false;
                  return { ...responseItem, body: newBody };
                }),
              );
            } else if (type === SSE_EVENT_TYPES.MESSAGE_COMPLETE) {
              const fullText: string = payload?.data?.full_text ?? '';
              if (isSubAgent) {
                const label = fullText.length > 60 ? `${fullText.slice(0, 60)}…` : fullText;
                const detail = fullText.length > 60 ? fullText : undefined;
                pushTraceStep({ kind: 'thought', label, detail });
              } else {
                streamingPendingReset.current = true;
                updateStreamingCard({ body: fullText });
              }
            } else if (type === SSE_EVENT_TYPES.SUBAGENT_INVOKED) {
              const agentName: string = payload?.data?.subagent_name ?? '';
              pushTraceStep({ kind: 'action', label: `Dispatching ${agentName}` });
            } else if (type === SSE_EVENT_TYPES.TOOL_CALLED) {
              const toolName: string = payload?.data?.tool_name ?? 'tool';
              pushTraceStep({ kind: 'tool_call', label: `${toolName}()` });
            } else if (tier === 'operational' && type.endsWith('.completed')) {
              const canvasSummary: string = payload?.data?.summary ?? '';
              pushTraceStep({ kind: 'observation', label: canvasSummary });
            } else if (
              type === SSE_EVENT_TYPES.ERROR_SYSTEM ||
              type === SSE_EVENT_TYPES.CAP_FIRED
            ) {
              updateStreamingCard({
                variant: RESPONSE_VARIANTS.WARNING,
                label: routingAiChat.cardLabels.headsUp,
                body:
                  type === SSE_EVENT_TYPES.CAP_FIRED
                    ? routingAiChat.chatCapError
                    : routingAiChat.chatStreamError,
                streaming: false,
              });
              setIsStreaming(false);
            }
          },
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          updateStreamingCard({
            variant: RESPONSE_VARIANTS.WARNING,
            label: routingAiChat.cardLabels.headsUp,
            body: routingAiChat.chatStreamError,
            streaming: false,
          });
        }
      } finally {
        streamingCardIdRef.current = null;
        abortControllerRef.current = null;
        setIsStreaming(false);
        setThreadItems((current) =>
          current.map((item): ThreadItem => {
            if (item.id !== streamingCardId) return item;
            const responseItem = item as ResponseThreadItem;
            if (responseItem.variant === RESPONSE_VARIANTS.WARNING) {
              return { ...responseItem, streaming: false };
            }
            return {
              ...responseItem,
              streaming: false,
              variant: RESPONSE_VARIANTS.READY,
              label: routingAiChat.cardLabels.ready,
            };
          }),
        );
      }
    },
    [
      appendWorkspaceError,
      draft,
      getWorkspaceErrorBody,
      isStreaming,
      isWorkspaceConnecting,
      routingAiChat,
      user.aiChatWorkspaceId,
    ],
  );

  const processCsvFile = useCallback(
    (file: File) => {
      const appendWarning = (body: string) => {
        setThreadItems((current) => [
          ...current,
          {
            id: nextMessageId.current++,
            type: THREAD_ITEM_TYPES.RESPONSE,
            variant: RESPONSE_VARIANTS.WARNING,
            label: routingAiChat.cardLabels.headsUp,
            body,
            actions: [],
          },
        ]);
      };

      if (isWorkspaceConnecting || !user.aiChatWorkspaceId) {
        appendWorkspaceError(getWorkspaceErrorBody());
        return;
      }

      if (!isAcceptedFileType(file)) {
        appendWarning(routingAiChat.uploadCsv.invalidFile);
        return;
      }

      if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
        appendWarning(routingAiChat.uploadCsv.fileTooLarge);
        return;
      }

      const reader = new FileReader();
      reader.onerror = () => {
        appendWarning(routingAiChat.uploadCsv.readError);
      };
      reader.onload = async (e) => {
        const csvText = (e.target?.result as string) || '';
        const promptId = nextMessageId.current++;
        const loadingId = nextMessageId.current++;

        setThreadItems((current) => [
          ...current,
          {
            id: promptId,
            type: THREAD_ITEM_TYPES.PROMPT,
            text: getUploadPromptText(file),
          },
          {
            id: loadingId,
            type: THREAD_ITEM_TYPES.RESPONSE,
            variant: RESPONSE_VARIANTS.ROUTING,
            label: routingAiChat.cardLabels.routingAi,
            body: routingAiChat.uploadCsv.uploading,
            actions: [],
            progress: 0,
          },
        ]);

        try {
          const response = await uploadCsvToAiWorkspace(
            csvText,
            user.aiChatWorkspaceId,
            (progressEvent: { loaded: number; total?: number }) => {
              if (!progressEvent.total) return;

              const nextProgress = Math.min(
                100,
                Math.round((progressEvent.loaded / progressEvent.total) * 100),
              );

              setThreadItems((current) =>
                current.map((item) =>
                  item.id === loadingId
                    ? {
                        ...item,
                        body: `${routingAiChat.uploadCsv.uploading} ${nextProgress}%`,
                        progress: nextProgress,
                      }
                    : item,
                ),
              );
            },
          );
          const artifactInfo: ArtifactInfo | undefined = (response.data as ArtifactInfoDataResponse)
            ?.data;
          const successBody = artifactInfo?.ref
            ? sprintf(routingAiChat.uploadCsv.successTemplate, {
                artifact: artifactInfo.ref.artifact,
                version: artifactInfo.ref.version,
              })
            : routingAiChat.uploadCsv.success;
          setThreadItems((current) =>
            current.map((item) =>
              item.id === loadingId
                ? {
                    ...item,
                    variant: RESPONSE_VARIANTS.READY,
                    label: routingAiChat.cardLabels.ready,
                    body: successBody,
                    actions: [],
                    progress: undefined,
                  }
                : item,
            ),
          );
        } catch (error) {
          const status = axios.isAxiosError(error) ? error.response?.status : undefined;
          setThreadItems((current) =>
            current.map((item) =>
              item.id === loadingId
                ? {
                    ...item,
                    variant: RESPONSE_VARIANTS.WARNING,
                    label: routingAiChat.cardLabels.headsUp,
                    body: getUploadErrorBody(status, routingAiChat),
                    actions: [],
                    progress: undefined,
                  }
                : item,
            ),
          );
        }
      };
      reader.readAsText(file);
    },
    [
      appendWorkspaceError,
      getUploadPromptText,
      getWorkspaceErrorBody,
      isWorkspaceConnecting,
      routingAiChat,
      user.aiChatWorkspaceId,
    ],
  );

  const handleUploadClick = useCallback(() => {
    if (isWorkspaceConnecting || !user.aiChatWorkspaceId) {
      appendWorkspaceError(getWorkspaceErrorBody());
      return;
    }

    fileInputRef.current?.click();
  }, [appendWorkspaceError, getWorkspaceErrorBody, isWorkspaceConnecting, user.aiChatWorkspaceId]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) processCsvFile(file);
      event.target.value = '';
    },
    [processCsvFile],
  );

  const handleDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (panelRef.current && !panelRef.current.contains(event.relatedTarget as Node)) {
      setIsDraggingOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setIsDraggingOver(false);
      const file = event.dataTransfer.files?.[0];
      if (file) processCsvFile(file);
    },
    [processCsvFile],
  );

  useEffect(() => {
    const isOpen = user.openPanel === PANELS.ROUTING_AI_CHAT;
    setShowPanel(isOpen);
    if (isOpen && !user.aiChatWorkspaceId) {
      initWorkspace();
    }
  }, [initWorkspace, user.openPanel]);

  useEffect(() => {
    resetConversation();
  }, [summary.key]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [showPanel, threadItems]);

  return {
    showPanel,
    draft,
    setDraft,
    isDraggingOver,
    isStreaming,
    isWorkspaceConnecting,
    isWorkspaceReady: Boolean(user.aiChatWorkspaceId),
    threadItems,
    selectedActionsByMessage,
    welcomeCard,
    bottomRef,
    panelRef,
    fileInputRef,
    handleClose,
    handleRestart,
    handleCardAction,
    handleSubmit,
    handleUploadClick,
    handleFileChange,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};

export default useRoutingAIChat;
